/* iter-palette-preview + iter-palette-commit + iter-accent-preview-tab
 * + iter-palette-custom + iter-palette-contrast
 *
 * Four-tab palette lab.
 *   • curated swatches per token (background / primary / accent / border)
 *   • custom-hex input per tab (`iter-palette-custom`)
 *   • WCAG contrast banner when the effective --background / --primary
 *     pair falls below AA (4.5:1) (`iter-palette-contrast`)
 *   • "Save as ship default" persists all previewed picks to
 *     `app_config.theme_*_hsl` so every page load paints with them
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Palette, RotateCcw, Check, Save, Loader2, AlertTriangle, History, Bookmark } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/api';
import { applyThemePreview, resetThemeToShipDefaults, ensureFocusRingVisible } from '@/lib/theme';

// ---------- Colour math ----------------------------------------------
const _hex = (v) => v.toString(16).padStart(2, '0');

function hexToRgb01(hex) {
  const raw = hex.replace('#', '');
  return {
    r: parseInt(raw.slice(0, 2), 16) / 255,
    g: parseInt(raw.slice(2, 4), 16) / 255,
    b: parseInt(raw.slice(4, 6), 16) / 255,
  };
}

// Hex → "H S% L%" (Tailwind CSS-variable format).
function hexToHslTuple(hex) {
  const { r, g, b } = hexToRgb01(hex);
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// "H S% L%" tuple → "#RRGGBB" — needed to compute contrast when the
// user has only picked one side and the other is still on the ship
// default (which we only have as an HSL variable).
function hslTupleToHex(tuple) {
  const m = /^(\d{1,3})\s+(\d{1,3})%\s+(\d{1,3})%$/.exec(String(tuple || '').trim());
  if (!m) return null;
  const h = (+m[1] % 360) / 360;
  const s = (+m[2]) / 100;
  const l = (+m[3]) / 100;
  if (s === 0) {
    const v = Math.round(l * 255);
    return '#' + _hex(v) + _hex(v) + _hex(v);
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const conv = (t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const r = Math.round(conv(h + 1 / 3) * 255);
  const g = Math.round(conv(h) * 255);
  const b = Math.round(conv(h - 1 / 3) * 255);
  return '#' + _hex(r) + _hex(g) + _hex(b);
}

// WCAG 2.1 relative luminance + contrast ratio.
function relativeLuminance(hex) {
  const { r, g, b } = hexToRgb01(hex);
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
function contrastRatio(hex1, hex2) {
  const l1 = relativeLuminance(hex1);
  const l2 = relativeLuminance(hex2);
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

// Read the currently-effective token as a hex string (whether the
// value is a user pick or the ship default).
function effectiveHex(tokenKey, picks) {
  if (picks[tokenKey]?.hex) return picks[tokenKey].hex;
  if (typeof document === 'undefined') return null;
  const cur = getComputedStyle(document.documentElement)
    .getPropertyValue(`--${tokenKey}`).trim();
  return hslTupleToHex(cur);
}

// ---------- Data ------------------------------------------------------
const CANDIDATES = {
  background: [
    { label: 'Tea Biscuit',      hex: '#F4EAE0', note: 'Currently shipping — warm cream · 30° hue' },
    { label: 'Manuscript Ivory', hex: '#F8F1E4', note: 'Lighter, closer to fresh paper'            },
    { label: 'Butter Cream',     hex: '#EFE4CB', note: 'Deeper amber undertone — cozier'           },
  ],
  primary: [
    { label: 'Copper (shipping)', hex: '#C67222', note: 'Warm amber ink · 28°'                     },
    { label: 'Oxblood',           hex: '#7A1B26', note: 'Moodier · aligns with header seal'         },
    { label: 'Forest',            hex: '#3E5C41', note: 'Cool contrast on Tea Biscuit'              },
  ],
  accent: [
    { label: 'Copper (shipping)', hex: '#CF8737', note: 'Current accent'                            },
    { label: 'Gold',              hex: '#B48A26', note: 'Warmer highlight — brass tone'             },
    { label: 'Rose',              hex: '#B85068', note: 'Softer, complementary to Tea Biscuit'      },
  ],
  border: [
    { label: 'Sand (shipping)', hex: '#CDBBA0', note: 'Current — 20% chroma · reads on Tea Biscuit' },
    { label: 'Charcoal',        hex: '#8A7B67', note: 'Higher contrast for dense tables'            },
    { label: 'Whisper',         hex: '#E7DECF', note: 'Softer separators — near invisible'          },
  ],
};

const TABS = [
  { key: 'background', label: 'Background' },
  { key: 'primary',    label: 'Primary'    },
  { key: 'accent',     label: 'Accent'     },
  { key: 'border',     label: 'Border'     },
];

const HEX_RE = /^#?([0-9a-fA-F]{6})$/;

// ---------- Component -------------------------------------------------
export default function PalettePreviewDialog({ open, onOpenChange }) {
  const [tab, setTab] = useState('background');
  const [picks, setPicks] = useState({});
  const [saving, setSaving] = useState(false);
  const [customIn, setCustomIn] = useState({ background: '', primary: '', accent: '', border: '' });
  // iter-palette-named + iter-palette-history
  const [named, setNamed] = useState([]);
  const [history, setHistory] = useState([]);
  const [nameInput, setNameInput] = useState('');
  const loadPalettes = () => {
    adminApi.palettesList()
      .then((r) => { setNamed(r.named || []); setHistory(r.history || []); })
      .catch(() => { /* silent */ });
  };
  useEffect(() => { if (open) loadPalettes(); }, [open]);

  useEffect(() => { if (!open) { setPicks({}); setNameInput(''); } }, [open]);

  const apply = (tokenKey, hex) => {
    const tuple = hexToHslTuple(hex);
    applyThemePreview({ [tokenKey]: tuple });
    ensureFocusRingVisible();
    setPicks((p) => ({ ...p, [tokenKey]: { hex, tuple } }));
    toast.success(`Previewing ${hex} on --${tokenKey}. Refresh reverts unless you Save.`);
  };
  const applyCustom = (tokenKey) => {
    const raw = (customIn[tokenKey] || '').trim();
    if (!HEX_RE.test(raw)) {
      toast.error('Enter a 6-digit hex like #C67222.');
      return;
    }
    const normalised = '#' + raw.replace('#', '').toUpperCase();
    apply(tokenKey, normalised);
  };
  const reset = () => {
    resetThemeToShipDefaults();
    setPicks({});
    setCustomIn({ background: '', primary: '', accent: '', border: '' });
    toast.info('Preview reset to shipping default.');
  };
  const saveAll = async () => {
    if (Object.keys(picks).length === 0) {
      toast.info('Nothing to save — pick at least one swatch first.');
      return;
    }
    setSaving(true);
    try {
      const patch = {};
      if (picks.background) patch.theme_background_hsl = picks.background.tuple;
      if (picks.primary)    patch.theme_primary_hsl    = picks.primary.tuple;
      if (picks.accent)     patch.theme_accent_hsl     = picks.accent.tuple;
      if (picks.border)     patch.theme_border_hsl     = picks.border.tuple;
      await adminApi.saveConfig(patch);
      toast.success('Saved. Every page load will use these colors now.');
      loadPalettes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  // iter-palette-named — Save the currently-previewed values under a name.
  const saveAsNamed = async () => {
    const name = nameInput.trim();
    if (!name) { toast.error('Enter a palette name first.'); return; }
    if (Object.keys(picks).length === 0) {
      toast.error('Preview at least one swatch before saving as named.');
      return;
    }
    try {
      await adminApi.palettesSaveNamed({
        name,
        background: picks.background?.tuple || null,
        primary:    picks.primary?.tuple    || null,
        accent:     picks.accent?.tuple     || null,
        border:     picks.border?.tuple     || null,
      });
      toast.success(`Saved as "${name}".`);
      setNameInput('');
      loadPalettes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save-as-named failed.');
    }
  };
  // iter-palette-named — Activate a named palette live + as ship default.
  const activateNamed = async (p) => {
    const tuple = {};
    if (p.background) tuple.background = p.background;
    if (p.primary)    tuple.primary    = p.primary;
    if (p.accent)     tuple.accent     = p.accent;
    if (p.border)     tuple.border     = p.border;
    applyThemePreview(tuple);
    ensureFocusRingVisible();
    setPicks({
      ...(p.background ? { background: { hex: '#custom', tuple: p.background } } : {}),
      ...(p.primary    ? { primary:    { hex: '#custom', tuple: p.primary    } } : {}),
      ...(p.accent     ? { accent:     { hex: '#custom', tuple: p.accent     } } : {}),
      ...(p.border     ? { border:     { hex: '#custom', tuple: p.border     } } : {}),
    });
    toast.info(`Previewing "${p.name}". Hit Save to commit as ship default.`);
  };
  const deleteNamed = async (p) => {
    if (!window.confirm(`Delete palette "${p.name}"?`)) return;
    try {
      await adminApi.palettesDeleteNamed(p.id);
      toast.success('Deleted.');
      loadPalettes();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed.'); }
  };
  // iter-palette-history — Revert to any of the last 10 saved snapshots.
  const revertHistory = async (h) => {
    if (!window.confirm(`Revert to the palette saved at ${new Date(h.at).toLocaleString()}? This overwrites current colors.`)) return;
    try {
      await adminApi.saveConfig({
        theme_background_hsl: h.background || '',
        theme_primary_hsl:    h.primary    || '',
        theme_accent_hsl:     h.accent     || '',
        theme_border_hsl:     h.border     || '',
      });
      applyThemePreview({
        background: h.background || null,
        primary:    h.primary    || null,
        accent:     h.accent     || null,
        border:     h.border     || null,
      });
      ensureFocusRingVisible();
      toast.success('Reverted.');
      loadPalettes();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Revert failed.'); }
  };

  // iter-palette-contrast — WCAG check on the effective bg/primary
  // combo. Recomputes on every render so a swatch click or custom
  // entry flips the banner immediately.
  const contrast = useMemo(() => {
    const bg = effectiveHex('background', picks);
    const pr = effectiveHex('primary', picks);
    if (!bg || !pr) return null;
    const ratio = contrastRatio(bg, pr);
    return { bg, pr, ratio };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picks, open, tab]);

  // iter-text-on-primary — Second contrast row: --primary × --primary-foreground.
  const textOnPrimary = useMemo(() => {
    if (typeof document === 'undefined') return null;
    const pr = effectiveHex('primary', picks);
    const fgTuple = getComputedStyle(document.documentElement)
      .getPropertyValue('--primary-foreground').trim();
    const fg = hslTupleToHex(fgTuple);
    if (!pr || !fg) return null;
    return { primary: pr, fg, ratio: contrastRatio(pr, fg) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picks, open, tab]);

  const gradeBadge = (ratio) => {
    if (ratio >= 7)   return { tone: 'emerald', label: 'AAA',      help: 'Comfortable for body text at every size.' };
    if (ratio >= 4.5) return { tone: 'emerald', label: 'AA',       help: 'Meets accessibility target.' };
    if (ratio >= 3)   return { tone: 'amber',   label: 'AA Large', help: 'Only large / bold text passes AA.' };
    return                 { tone: 'rose',    label: 'FAIL',     help: 'Below 3:1 — text on this pair is unreadable.' };
  };
  const contrastBadge = contrast ? gradeBadge(contrast.ratio) : null;
  const textBadge     = textOnPrimary ? gradeBadge(textOnPrimary.ratio) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl" data-testid="palette-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="h-4 w-4" /> Preview palette
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11.5px] text-muted-foreground">
          Audit each theme token side-by-side. Click a swatch or paste a custom hex to preview live. Hit
          <b> Save as ship default </b>to persist your picks to <code className="rounded-sm bg-muted px-1">app_config</code>.
        </p>

        {/* iter-palette-contrast — WCAG banner. */}
        {contrast && contrastBadge && (
          <div
            className={
              'flex items-start gap-2 rounded-md border p-2.5 text-[11.5px] '
              + (contrastBadge.tone === 'rose'
                  ? 'border-rose-500/60 bg-rose-50 text-rose-900'
                  : contrastBadge.tone === 'amber'
                    ? 'border-amber-500/60 bg-amber-50 text-amber-900'
                    : 'border-emerald-500/60 bg-emerald-50 text-emerald-900')
            }
            data-testid="palette-contrast-banner"
            data-tone={contrastBadge.tone}
          >
            {contrastBadge.tone === 'rose' && <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold">Body text (bg × primary):</span>
                <span className="font-mono-stat" data-testid="palette-contrast-ratio">
                  {contrast.ratio.toFixed(2)}:1
                </span>
                <span className="rounded-sm bg-white/70 px-1 text-[10px] uppercase tracking-wider">
                  {contrastBadge.label}
                </span>
              </div>
              <div className="mt-0.5 text-[10.5px] opacity-80">
                {contrastBadge.help}
                {contrast.ratio < 4.5 && ' Aim for ≥ 4.5:1 (WCAG AA). Try a darker Primary or lighter Background.'}
              </div>
            </div>
            <div className="ml-auto flex overflow-hidden rounded-sm border border-white/60">
              <span className="h-6 w-8" style={{ background: contrast.bg }} />
              <span className="flex h-6 w-16 items-center justify-center font-semibold" style={{ background: contrast.bg, color: contrast.pr }}>Aa</span>
              <span className="h-6 w-8" style={{ background: contrast.pr }} />
            </div>
          </div>
        )}

        {/* iter-text-on-primary — Button-label contrast row. */}
        {textOnPrimary && textBadge && (
          <div
            className={
              'mt-2 flex items-start gap-2 rounded-md border p-2.5 text-[11.5px] '
              + (textBadge.tone === 'rose'
                  ? 'border-rose-500/60 bg-rose-50 text-rose-900'
                  : textBadge.tone === 'amber'
                    ? 'border-amber-500/60 bg-amber-50 text-amber-900'
                    : 'border-emerald-500/60 bg-emerald-50 text-emerald-900')
            }
            data-testid="palette-text-on-primary-banner"
            data-tone={textBadge.tone}
          >
            {textBadge.tone === 'rose' && <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold">Button labels (primary × primary-fg):</span>
                <span className="font-mono-stat" data-testid="palette-text-on-primary-ratio">
                  {textOnPrimary.ratio.toFixed(2)}:1
                </span>
                <span className="rounded-sm bg-white/70 px-1 text-[10px] uppercase tracking-wider">
                  {textBadge.label}
                </span>
              </div>
              <div className="mt-0.5 text-[10.5px] opacity-80">
                {textBadge.help} Button copy sits on this pair.
              </div>
            </div>
            <div className="ml-auto flex overflow-hidden rounded-sm border border-white/60">
              <span className="flex h-6 items-center justify-center px-2 font-semibold" style={{ background: textOnPrimary.primary, color: textOnPrimary.fg }}>Save</span>
            </div>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="mt-3">
          <TabsList className="grid w-full grid-cols-4">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`palette-tab-${t.key}`}>
                {t.label}
                {picks[t.key] && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-primary" />}
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-3 space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {CANDIDATES[t.key].map((c) => {
                  const active = picks[t.key]?.hex === c.hex;
                  return (
                    <button
                      key={c.hex}
                      type="button"
                      onClick={() => apply(t.key, c.hex)}
                      data-testid={`palette-swatch-${t.key}-${c.hex.slice(1)}`}
                      className={
                        'group flex flex-col items-stretch overflow-hidden rounded-md border-2 text-left transition '
                        + (active ? 'border-primary shadow-panel-active' : 'border-border hover:border-primary/60')
                      }
                    >
                      <div className="h-20 w-full" style={{ background: c.hex }} />
                      <div className="border-t border-border bg-card px-3 py-2">
                        <div className="flex items-center justify-between gap-1">
                          <div className="text-[12px] font-semibold">{c.label}</div>
                          {active && <Check className="h-3.5 w-3.5 text-primary" />}
                        </div>
                        <div className="mt-0.5 font-mono-stat text-[10.5px] text-muted-foreground">{c.hex}</div>
                        <div className="mt-1 text-[10.5px] text-muted-foreground">{c.note}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {/* iter-palette-custom — free-form hex entry. */}
              <div className="rounded-md border border-dashed border-border bg-card/50 p-2.5">
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Custom hex — not limited to the curated three
                </div>
                <div className="mt-1.5 flex items-center gap-2">
                  <Input
                    value={customIn[t.key]}
                    onChange={(e) => setCustomIn((s) => ({ ...s, [t.key]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') applyCustom(t.key); }}
                    placeholder="#RRGGBB (e.g. #C67222)"
                    className="h-8 flex-1 font-mono-stat text-[12px]"
                    maxLength={7}
                    data-testid={`palette-custom-${t.key}-input`}
                  />
                  <div
                    className="h-8 w-8 shrink-0 rounded-sm border border-border"
                    style={{ background: HEX_RE.test(customIn[t.key] || '')
                        ? '#' + (customIn[t.key] || '').replace('#', '')
                        : 'transparent' }}
                    aria-hidden
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => applyCustom(t.key)}
                    disabled={!HEX_RE.test(customIn[t.key] || '')}
                    className="h-8 gap-1 text-[11.5px]"
                    data-testid={`palette-custom-${t.key}-apply`}
                  >
                    Preview
                  </Button>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>

        <div className="mt-4 flex flex-col gap-2 border-t border-border pt-3">
          {/* iter-palette-named — Named palettes row */}
          <div className="flex flex-wrap items-center gap-2" data-testid="palette-named-row">
            <Bookmark className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-[11px] font-semibold text-muted-foreground">Named palettes:</span>
            {named.length === 0 && <span className="text-[10.5px] text-muted-foreground">none yet</span>}
            {named.slice(0, 6).map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => activateNamed(p)}
                onDoubleClick={() => deleteNamed(p)}
                title="Click to preview · double-click to delete"
                data-testid={`palette-named-${p.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-panel-header/60 px-2 py-0.5 text-[10.5px] hover:bg-panel-header"
              >
                {p.name}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-1.5">
              <Input
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveAsNamed(); }}
                placeholder="Name (e.g. Autumn Draft)"
                className="h-7 w-40 text-[11px]"
                data-testid="palette-named-input"
              />
              <Button size="sm" variant="outline" onClick={saveAsNamed} className="h-7 gap-1 text-[10.5px]" data-testid="palette-named-save">
                <Bookmark className="h-3 w-3" /> Save as named
              </Button>
            </div>
          </div>

          {/* iter-palette-history — Last-10 revert list */}
          {history.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="palette-history-row">
              <History className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-[11px] font-semibold text-muted-foreground">Recent saves (revert):</span>
              {history.slice(0, 10).map((h, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => revertHistory(h)}
                  title={`Revert to ${new Date(h.at).toLocaleString()}\nbg=${h.background||'—'} pr=${h.primary||'—'}`}
                  data-testid={`palette-history-${i}`}
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-panel-header/40 px-2 py-0.5 font-mono-stat text-[10px] text-muted-foreground hover:bg-panel-header hover:text-foreground"
                >
                  {new Date(h.at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="text-[10.5px] text-muted-foreground">
              {Object.keys(picks).length === 0
                ? 'No previews yet.'
                : `Previewing: ${Object.keys(picks).join(' · ')}`}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={reset} className="gap-1.5" data-testid="palette-preview-reset">
                <RotateCcw className="h-3.5 w-3.5" /> Reset
              </Button>
              <Button size="sm" onClick={saveAll} disabled={saving || Object.keys(picks).length === 0} className="gap-1.5" data-testid="palette-preview-save">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save as ship default
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
